import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ITeam } from '../../../models/Team';
import { IAFINotification, IAFITeamEntry } from '../../../models/afi';
import { antifragilityEndpoints } from '../../../endpoints/antifragility';
import { AFI_COMPONENT_LABELS, AFI_ALERT_LABELS, AFI_ALERT_SEVERITY_COLORS, getAFIColor } from '../../constants/afi';
import PageHelpButton from '../../../shared/PageHelpButton/PageHelpButton';
import AFITeamTable from './AFITeamTable';
import AFIAthleteView from './AFIAthleteView';
import AFIComponentDetail from './AFIComponentDetail';
import AFINotificationBanner from './AFINotificationBanner';
import styles from './AFITab.module.scss';

interface AFITabProps {
  team: ITeam<any>;
}

type AFIView =
  | { level: 1 }
  | { level: 2; athleteId: string; athleteName: string }
  | { level: 3; athleteId: string; athleteName: string; component: 'A' | 'B' | 'C' };

const AFITab: React.FC<AFITabProps> = ({ team }) => {
  const [afiView, setAFIView] = useState<AFIView>({ level: 1 });
  const [showNotifications, setShowNotifications] = useState(false);
  const [modalAthlete, setModalAthlete] = useState<IAFITeamEntry | null>(null);

  const { data: teamAFIData, isLoading, isError } = useQuery({
    queryKey: ['afi', 'team', team._id],
    queryFn: async () => {
      const { data } = await antifragilityEndpoints.getTeamAFI(team._id);
      return data;
    },
    enabled: true,
    staleTime: 1000 * 60 * 5,
    retry: false,
  });

  const goToAthlete = (athleteId: string, athleteName: string) =>
    setAFIView({ level: 2, athleteId, athleteName });

  const goToComponent = (component: 'A' | 'B' | 'C') => {
    if (afiView.level === 2 || afiView.level === 3) {
      setAFIView({
        level: 3,
        athleteId: afiView.athleteId,
        athleteName: afiView.athleteName,
        component,
      });
    }
  };

  const goBack = () => {
    if (afiView.level === 3) {
      setAFIView({
        level: 2,
        athleteId: afiView.athleteId,
        athleteName: afiView.athleteName,
      });
    } else {
      setAFIView({ level: 1 });
    }
  };

  const renderBreadcrumb = () => {
    if (afiView.level === 1) {
      return (
        <div className={styles.breadcrumb}>
          <span className={styles.breadcrumbCurrent}>Dashboard AFI</span>
          <PageHelpButton pageKey="page-afi" />
        </div>
      );
    }

    if (afiView.level === 2) {
      return (
        <div className={styles.breadcrumb}>
          <span
            className={styles.breadcrumbLink}
            onClick={() => setAFIView({ level: 1 })}
          >
            ← Team
          </span>
          <span className={styles.breadcrumbSep}>/</span>
          <span className={styles.breadcrumbCurrent}>{afiView.athleteName}</span>
        </div>
      );
    }

    if (afiView.level === 3) {
      return (
        <div className={styles.breadcrumb}>
          <span
            className={styles.breadcrumbLink}
            onClick={() => setAFIView({ level: 1 })}
          >
            ← Team
          </span>
          <span className={styles.breadcrumbSep}>/</span>
          <span
            className={styles.breadcrumbLink}
            onClick={() =>
              setAFIView({
                level: 2,
                athleteId: afiView.athleteId,
                athleteName: afiView.athleteName,
              })
            }
          >
            {afiView.athleteName}
          </span>
          <span className={styles.breadcrumbSep}>/</span>
          <span className={styles.breadcrumbCurrent}>
            {AFI_COMPONENT_LABELS[afiView.component]}
          </span>
        </div>
      );
    }

    return null;
  };

  const allNotifications: IAFINotification[] = teamAFIData?.athletes
    ? teamAFIData.athletes.flatMap((a) =>
        (a.alerts ?? []).map((alert) => ({
          ...alert,
          athleteId: a.athleteId,
          athleteName: a.athleteName,
          read: false,
        }))
      )
    : [];

  return (
    <div className={styles.container}>
      {renderBreadcrumb()}

      {afiView.level === 1 && (
        <>
          <AFINotificationBanner
            teamId={team._id}
            onViewAll={() => setShowNotifications(true)}
          />
          {isError ? (
            <div style={{ padding: '24px 0', color: 'var(--c-text-1)', fontFamily: "'Satoshi', sans-serif", fontSize: 14 }}>
              Dati AFI non ancora disponibili per questo team.
            </div>
          ) : (
            <AFITeamTable
              athletes={teamAFIData?.athletes ?? []}
              isLoading={isLoading}
              onSelectAthlete={(athleteId, athleteName) => {
                const entry = (teamAFIData?.athletes ?? []).find(
                  (a) => a.athleteId === athleteId,
                );
                if (entry) setModalAthlete(entry);
              }}
            />
          )}
        </>
      )}

      {afiView.level === 2 && (
        <AFIAthleteView
          teamId={team._id}
          athleteId={afiView.athleteId}
          athleteName={afiView.athleteName}
          onGoToComponent={goToComponent}
        />
      )}

      {afiView.level === 3 && (
        <AFIComponentDetail
          teamId={team._id}
          athleteId={afiView.athleteId}
          component={afiView.component}
        />
      )}

      {modalAthlete && (
        <div className={styles.modalOverlay} onClick={() => setModalAthlete(null)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>
                {modalAthlete.athleteName || 'Atleta'}
              </h3>
              <button
                className={styles.closeBtn}
                onClick={() => setModalAthlete(null)}
                aria-label="Chiudi"
              >
                &times;
              </button>
            </div>

            <div className={styles.modalBody}>
              <div className={styles.modalScore}>
                <div className={styles.modalScoreValue} style={{ color: getAFIColor(modalAthlete.afiScore) }}>
                  {modalAthlete.afiScore}
                </div>
                <div className={styles.modalScoreLabel}>AFI Score</div>
                <div className={styles.modalClass}>{modalAthlete.afiClass}</div>
              </div>

              <div className={styles.modalComponents}>
                {[
                  { label: 'A — Stabilita\u0300 Fisiologica', value: modalAthlete.componentA, weight: '40%' },
                  { label: 'B — Capacita\u0300 Recovery', value: modalAthlete.componentB, weight: '40%' },
                  { label: 'C — Margine Strutturale', value: modalAthlete.componentC, weight: '20%' },
                ].map((comp) => (
                  <div key={comp.label} className={styles.modalComp}>
                    <div className={styles.modalCompHeader}>
                      <span className={styles.modalCompLabel}>{comp.label}</span>
                      <span className={styles.modalCompWeight}>{comp.weight}</span>
                    </div>
                    <div className={styles.modalCompBar}>
                      <div
                        className={styles.modalCompFill}
                        style={{
                          width: comp.value != null ? `${comp.value}%` : '0%',
                          backgroundColor: comp.value != null ? getAFIColor(comp.value) : '#e7e7e7',
                        }}
                      />
                    </div>
                    <div className={styles.modalCompValue}>
                      {comp.value != null ? `${comp.value}` : 'N/D'}
                    </div>
                  </div>
                ))}
              </div>

              {(modalAthlete.componentA == null || modalAthlete.componentB == null || modalAthlete.componentC == null) && (
                <div className={styles.modalWarning}>
                  Dati insufficienti per un AFI completo. Score parziale.
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showNotifications && (
        <div className={styles.notificationsOverlay}>
          <div className={styles.notificationsHeader}>
            <h4>Notifiche AFI</h4>
            <button
              className={styles.closeBtn}
              onClick={() => setShowNotifications(false)}
              aria-label="Chiudi pannello notifiche"
            >
              ✕
            </button>
          </div>

          {allNotifications.length === 0 ? (
            <p style={{ color: 'var(--c-text-1)', fontSize: 14 }}>Nessuna notifica attiva.</p>
          ) : (
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {allNotifications.map((notif, idx) => (
                <li
                  key={idx}
                  style={{
                    borderLeft: `3px solid ${AFI_ALERT_SEVERITY_COLORS[notif.severity]}`,
                    padding: '10px 12px',
                    marginBottom: 10,
                    background: 'var(--c-app-bg)',
                    borderRadius: 4,
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      marginBottom: 4,
                    }}
                  >
                    <span
                      style={{
                        fontSize: 12,
                        fontWeight: 700,
                        color: AFI_ALERT_SEVERITY_COLORS[notif.severity],
                      }}
                    >
                      {AFI_ALERT_LABELS[notif.type]}
                    </span>
                    <span style={{ fontSize: 11, color: 'var(--c-text-1)' }}>{notif.date}</span>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--c-text-0)', marginBottom: 2 }}>
                    {notif.athleteName}
                  </div>
                  <p style={{ fontSize: 13, color: 'var(--c-text-1)', margin: 0 }}>
                    {notif.message}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default AFITab;
